/**
 * O tabuleiro: mapa, unidades, cursor e as sobreposições de alcance.
 *
 * Toda a parte visual do jogo mora aqui, em Pixi. A interface de janelas e
 * menus fica em React, por cima — cada uma na camada em que é boa. O React
 * empurra estado para cá; daqui não sai nada além de eventos de ponteiro.
 */

import {
  Application, Assets, ColorMatrixFilter, Container, Graphics, Sprite, Texture,
} from 'pixi.js';
import { CONDITIONS, normalizeSize, type MatchState, type Unit, type UnitSize, type Vec2 } from '@oz/rules';
import {
  ASSETS_BASE, battlerArt, frameRect, walkFrames, type Manifest, type SpriteGrid,
} from '../assets/manifest.js';
import { frameTexture } from '../render/frames.js';
import { mapArtBodyScale, mapArtFootprint } from '../render/map-art.js';
import {
  chestSprite, loadChestSheet, TilemapView, type MapData, type TilesetDef,
} from '../render/tilemap.js';
import { TILE_SIZE } from '../render/tiles.js';
import { LatestOnly } from './serial.js';
import type { UnitPresentation } from './setup.js';

/** Direções na ordem do acervo. */
const DOWN = 0;
const LEFT = 1;
const RIGHT = 2;
const UP = 3;

const COLOR_MOVE = 0x3f6fd8;
const COLOR_ATTACK = 0xd8443f;
const COLOR_HEAL = 0x3fb07a;
/*
 * A ameaça é roxa, e não vermelha como no Fire Emblem.
 *
 * Lá o vermelho está livre porque o alcance da SUA unidade não é vermelho; aqui
 * é (`COLOR_ATTACK`), e as duas manchas convivem na mesma tela o tempo todo —
 * a do inimigo aceso por baixo, a sua por cima, quando você levanta a unidade.
 * Duas leituras opostas no mesmo tom seriam a pior confusão possível: "posso
 * bater aqui" e "levo pancada aqui".
 */
const COLOR_THREAT = 0x8a2f6a;
/** O contorno e a marca da fonte: a mesma cor, clara o bastante para ler. */
const COLOR_THREAT_EDGE = 0xe064ad;

export type OverlayKind = 'movimento' | 'ataque' | 'cura' | 'ameaca';

/** A marca dos inimigos com o alcance à mostra, por cima de tudo. */
const THREAT_MARK_LABEL = 'ameaca-fonte';

/** Constante de tempo da câmera: quanto maior, mais preguiçosa a rolagem. */
const CAMERA_TAU_MS = 90;

const OVERLAY_COLOR: Record<OverlayKind, number> = {
  movimento: COLOR_MOVE,
  ataque: COLOR_ATTACK,
  cura: COLOR_HEAL,
  ameaca: COLOR_THREAT,
};

/**
 * A folha de caminhada de uma unidade, quando ela tem uma.
 *
 * Nem toda unidade tem: um monstro importado só traz o desenho de combate, e é
 * ele que vai ao tabuleiro. Nesse caso o corpo é uma imagem parada, e `walk`
 * fica nulo — é por ele que o resto do tabuleiro sabe se há quadros para
 * animar ou se o que existe é uma figura só.
 */
interface WalkSheet {
  readonly sheet: Texture;
  /** A grade DESTA folha. Nem toda folha tem o mesmo formato: as de um
   *  personagem só têm 3×4 quadros, as coletivas têm 12×8. */
  readonly grid: SpriteGrid;
  readonly character: number;
}

/** A folha de ícones do acervo: 16 colunas de ícones de 32 pixels. */
const ICON_SIDE = 32;
const ICON_COLUMNS = 16;
/** O tamanho da marca de condição sobre a unidade, em pixels do mapa. */
const MARK_SIDE = 16;
/** Quantas marcas cabem na cabeça de uma unidade de uma casa. */
const MAX_MARKS = 3;

interface UnitSprite {
  readonly root: Container;
  readonly body: Sprite;
  readonly hpBar: Graphics;
  /** Os ícones das condições, no canto de cima. */
  readonly marks: Container;
  /** O que as marcas mostram agora — para não redesenhar a cada sincronia. */
  markKey: string;
  readonly walk: WalkSheet | null;
  /** Quantas casas a unidade cobre — quase sempre uma. */
  readonly size: UnitSize;
  direction: number;
  step: number;
  /** Já agiu neste turno — guardado para não remontar o filtro a cada quadro. */
  spent: boolean;
}

export class Board {
  readonly app = new Application();

  #tilemap = new TilemapView();
  #world = new Container();
  #gates = new Container();
  #overlay = new Container();
  #pathLayer = new Container();
  /*
   * Os baús ficam ENTRE o caminho e as unidades.
   *
   * Abaixo das unidades porque o herói que abre um baú fica em cima dele — e
   * acima do traçado do caminho porque o baú é peça do tabuleiro, não marcação
   * de interface.
   */
  #chestLayer = new Container();
  #unitLayer = new Container();
  #cursor = new Container();
  #cursorMark = new Graphics();

  #sprites = new Map<number, UnitSprite>();
  /** A fila que impede duas sincronias de desenharem a mesma unidade. */
  #unitSync = new LatestOnly();
  /** A folha de ícones, carregada na primeira condição que aparecer. */
  #iconSheet: Promise<Texture | null> | null = null;
  /** Um filtro só para todas as unidades que já agiram — ver `#setSpent`. */
  #spentFilter: ColorMatrixFilter | null = null;
  #manifest: Manifest | null = null;
  #map: MapData | null = null;
  #cursorAt: Vec2 = { x: 0, y: 0 };
  #blink = 0;
  #walk = 0;
  #zoom = 1;
  #reducedMotion = false;
  // A câmera guarda para onde quer ir; o laço a leva até lá. Fixar a posição
  // direto no `#follow` fazia o mapa saltar a cada passo do cursor — era o
  // "rápido e pouco fluido" de sempre. O plano já pedia rolagem suave.
  #camX = 0;
  #camY = 0;
  #camReady = false;
  // `Application.init` é assíncrono e o React desmonta quando quiser. Destruir
  // uma aplicação Pixi que ainda não terminou de subir estoura em
  // "this._cancelResize is not a function" — e a exceção não fica contida no
  // tabuleiro: derruba a árvore React inteira, deixando a página em branco.
  #initialized = false;
  #destroyed = false;

  onTileClick: ((at: Vec2) => void) | null = null;
  onTileHover: ((at: Vec2) => void) | null = null;
  onStep: (() => void) | null = null;
  /** A unidade atravessou um portal em vez de dar um passo. */
  onTeleport: (() => void) | null = null;
  /** O navegador derrubou o WebGL do tabuleiro; ele não se desenha mais. */
  onContextLost: (() => void) | null = null;

  async init(
    host: HTMLElement,
    manifest: Manifest,
    map: MapData,
    tileset: TilesetDef,
  ): Promise<void> {
    this.#manifest = manifest;
    this.#map = map;
    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    await this.app.init({
      background: 0x0f1118,
      antialias: false,
      roundPixels: true,
      resizeTo: host,
      // Escala inteira em telas de alta densidade: meio pixel numa arte de
      // 48px é a diferença entre nítido e sujo.
      resolution: Math.max(1, Math.floor(window.devicePixelRatio || 1)),
      autoDensity: true,
    });

    await this.#tilemap.load(this.app.renderer, manifest, map, tileset);

    this.#world.addChild(
      this.#tilemap.container,
      this.#gates,
      this.#overlay,
      this.#pathLayer,
      this.#chestLayer,
      this.#unitLayer,
      // Copa de árvore, arco de portal e telhado ficam ACIMA das unidades: é o
      // que faz o soldado passar por baixo em vez de por cima.
      this.#tilemap.overlay,
      this.#cursor,
    );
    this.app.stage.addChild(this.#world);

    this.#buildCursor();
    this.#buildGates();
    this.#bindPointer();

    this.app.ticker.add((ticker) => this.#tick(ticker.deltaMS));

    this.#initialized = true;

    // Desmontou no meio da subida: agora que existe uma aplicação de verdade,
    // ela pode — e precisa — ser desfeita.
    if (this.#destroyed) {
      this.app.destroy(true, { children: true });
      return;
    }

    host.replaceChildren(this.app.canvas);
    this.app.canvas.style.imageRendering = 'pixelated';
    this.app.canvas.style.display = 'block';
    this.app.canvas.style.touchAction = 'none';
    this.app.canvas.dataset['ready'] = 'true';
    /*
     * Esperar o contexto voltar não devolve o mapa.
     *
     * O navegador pode derrubar o WebGL do tabuleiro a qualquer hora — GPU
     * reiniciada, a máquina que acordou do sono, contextos demais na página.
     * Mesmo quando ele devolve o contexto, o chão volta vazio: foi cozido numa
     * textura de GPU, e o conteúdo dela não é restaurado. Quem montou o
     * tabuleiro é avisado e monta outro.
     *
     * O `destroy` também derruba o contexto, de propósito; esse não é aviso.
     */
    this.app.canvas.addEventListener('webglcontextlost', (event) => {
      event.preventDefault();
      if (this.#destroyed) return;
      console.warn('Contexto WebGL do tabuleiro perdido; o tabuleiro será remontado.');
      this.onContextLost?.();
    });
  }

  // --- cursor -------------------------------------------------------------

  /** Quantas casas o cursor está abraçando no momento. */
  #cursorSpan: UnitSize = { w: 1, h: 1 };

  #buildCursor(span: UnitSize = this.#cursorSpan): void {
    // Cantos em L, e não um quadrado cheio: é a marca visual do cursor dos
    // Fire Emblem de GBA, e deixa ver o que está embaixo.
    //
    // O cursor abraça o CORPO de quem está debaixo dele, e não uma casa fixa:
    // sobre um monstro de duas por duas, um contorno de uma casa só deixaria o
    // jogador sem saber onde a criatura começa e onde acaba — e é justamente a
    // área dela que decide de onde se pode atacá-la.
    const arm = 14;
    const thickness = 3;
    const width = span.w * TILE_SIZE;
    const height = span.h * TILE_SIZE;
    const g = this.#cursorMark;
    g.clear();

    for (const [cx, cy] of [[0, 0], [1, 0], [0, 1], [1, 1]] as const) {
      const x = cx * width;
      const y = cy * height;
      g.rect(x + (cx === 0 ? 0 : -arm), y + (cy === 0 ? 0 : -thickness), arm, thickness);
      g.rect(x + (cx === 0 ? 0 : -thickness), y + (cy === 0 ? 0 : -arm), thickness, arm);
    }
    g.fill({ color: 0xffffff });
    if (g.parent === null) this.#cursor.addChild(g);
  }

  /**
   * Marca as bocas de teleporte do mapa.
   *
   * Um portal invisível seria pior do que nenhum: o jogador veria a unidade
   * sumir de um canto e reaparecer no outro sem entender por quê, e não teria
   * como planejar a jogada seguinte. O anel fica desenhado o tempo todo,
   * debaixo das sobreposições de alcance, e pulsa devagar para se distinguir
   * da arte do chão.
   */
  #buildGates(): void {
    this.#gates.removeChildren().forEach((c) => c.destroy());

    const gates = this.#map?.teleports ?? [];
    if (gates.length === 0) return;

    const g = new Graphics();
    const half = TILE_SIZE / 2;
    const outer = half - 4;

    for (const gate of gates) {
      for (const mouth of [gate.a, gate.b]) {
        const cx = mouth.x * TILE_SIZE + half;
        const cy = mouth.y * TILE_SIZE + half;

        // O fundo escuro é o que faz o anel existir: os tilesets do acervo vão
        // de areia clara a pedra verde-limão, e um anel claro sozinho some na
        // metade deles.
        g.circle(cx, cy, outer).fill({ color: 0x150b2e, alpha: 0.62 });
        g.circle(cx, cy, outer).stroke({ color: 0x1b1030, width: 4, alpha: 0.85 });
        g.circle(cx, cy, outer).stroke({ color: 0xd8b4fe, width: 2.5, alpha: 1 });
        g.circle(cx, cy, outer - 7).stroke({ color: 0xf5f3ff, width: 1.5, alpha: 0.9 });
        g.circle(cx, cy, 3).fill({ color: 0xf5f3ff, alpha: 0.95 });
      }
    }

    this.#gates.addChild(g);
  }

  setCursor(at: Vec2, span?: UnitSize | undefined): void {
    const body = normalizeSize(span);
    if (body.w !== this.#cursorSpan.w || body.h !== this.#cursorSpan.h) {
      this.#cursorSpan = body;
      this.#buildCursor(body);
    }
    this.#cursorAt = at;
    this.#cursor.position.set(at.x * TILE_SIZE, at.y * TILE_SIZE);
    this.#follow(at);
  }

  get cursor(): Vec2 {
    return this.#cursorAt;
  }

  // --- sobreposições ------------------------------------------------------

  setOverlay(kind: OverlayKind, tiles: readonly Vec2[]): void {
    const existing = this.#overlay.getChildByLabel(kind, false);
    existing?.destroy();
    if (tiles.length === 0) return;

    const g = new Graphics();
    g.label = kind;
    for (const tile of tiles) {
      g.rect(tile.x * TILE_SIZE, tile.y * TILE_SIZE, TILE_SIZE, TILE_SIZE);
    }
    g.fill({ color: OVERLAY_COLOR[kind], alpha: 0.34 });

    /*
     * A ameaça ganha contorno; as outras, não.
     *
     * As outras manchas nascem de uma unidade só, cabem na tela inteira de
     * relance e ficam SOB o cursor — dizem "daqui até aqui". A do inimigo é
     * outra coisa: espalhada, recortada pelo terreno, e é a borda dela que
     * decide a jogada. Uma casa a mais ou a menos é a diferença entre passar o
     * turno em paz e perder a unidade, e um preenchimento translúcido sobre
     * areia clara não diz onde ele acaba.
     */
    if (kind === 'ameaca') this.#strokeBorder(g, tiles, COLOR_THREAT_EDGE);

    this.#overlay.addChild(g);
  }

  /**
   * Os inimigos cujo alcance está à mostra, marcados no próprio corpo.
   *
   * Com três ou quatro acesos as manchas se fundem numa só, e some a resposta
   * para "quais eu liguei?" — sem ela, apagar o que sobra vira caça ao clique.
   */
  setThreatMarks(tiles: readonly Vec2[]): void {
    const existing = this.#overlay.getChildByLabel(THREAT_MARK_LABEL, false);
    existing?.destroy();
    if (tiles.length === 0) return;

    const g = new Graphics();
    g.label = THREAT_MARK_LABEL;
    // Uma moldura por dentro da casa: por fora ela encostaria na vizinha e as
    // duas viram um bloco só quando o monstro ocupa mais de uma casa.
    const inset = 2;
    for (const tile of tiles) {
      g.rect(
        tile.x * TILE_SIZE + inset,
        tile.y * TILE_SIZE + inset,
        TILE_SIZE - inset * 2,
        TILE_SIZE - inset * 2,
      );
    }
    g.stroke({ color: COLOR_THREAT_EDGE, width: 2, alpha: 0.95 });
    this.#overlay.addChild(g);
  }

  /**
   * Contorna o PERÍMETRO de um conjunto de casas.
   *
   * Só as arestas que dão para fora: traçar os quatro lados de cada casa
   * desenharia a grade inteira por dentro da mancha, que é ruído, e dobraria o
   * número de segmentos num alcance de exército inteiro.
   */
  #strokeBorder(g: Graphics, tiles: readonly Vec2[], color: number): void {
    const inside = new Set(tiles.map((t) => `${t.x},${t.y}`));
    const has = (x: number, y: number): boolean => inside.has(`${x},${y}`);

    for (const t of tiles) {
      const left = t.x * TILE_SIZE;
      const top = t.y * TILE_SIZE;
      const right = left + TILE_SIZE;
      const bottom = top + TILE_SIZE;

      if (!has(t.x, t.y - 1)) {
        g.moveTo(left, top);
        g.lineTo(right, top);
      }
      if (!has(t.x, t.y + 1)) {
        g.moveTo(left, bottom);
        g.lineTo(right, bottom);
      }
      if (!has(t.x - 1, t.y)) {
        g.moveTo(left, top);
        g.lineTo(left, bottom);
      }
      if (!has(t.x + 1, t.y)) {
        g.moveTo(right, top);
        g.lineTo(right, bottom);
      }
    }
    g.stroke({ color, width: 2, alpha: 0.9 });
  }

  clearOverlays(): void {
    this.#overlay.removeChildren().forEach((c) => c.destroy());
  }

  /** Rastro do caminho escolhido, do primeiro passo ao destino. */
  setPath(path: readonly Vec2[]): void {
    this.#pathLayer.removeChildren().forEach((c) => c.destroy());
    if (path.length < 2) return;

    const g = new Graphics();
    const half = TILE_SIZE / 2;
    const center = (p: Vec2): [number, number] => [p.x * TILE_SIZE + half, p.y * TILE_SIZE + half];

    g.moveTo(...center(path[0]!));
    for (let i = 1; i < path.length; i++) {
      const from = path[i - 1]!;
      const step = path[i]!;
      // O salto de teleporte não é um trecho de caminhada: ligar as duas bocas
      // com uma reta desenharia uma linha atravessando meio mapa, por cima de
      // paredes por onde ninguém passa.
      const walked = Math.abs(step.x - from.x) + Math.abs(step.y - from.y) === 1;
      if (walked) g.lineTo(...center(step));
      else g.moveTo(...center(step));
    }
    g.stroke({ color: 0xffe08a, width: 6, cap: 'round', join: 'round', alpha: 0.9 });

    const end = path[path.length - 1]!;
    g.circle(end.x * TILE_SIZE + half, end.y * TILE_SIZE + half, 7);
    g.fill({ color: 0xffe08a });

    this.#pathLayer.addChild(g);
  }

  // --- unidades -----------------------------------------------------------

  #movingUnitId: number | null = null;
  /** Unidade que o desenho já levou ao destino, antes de a jogada valer. */
  #previewUnitId: number | null = null;
  /** Onde o estado diz que cada unidade está — usado para desfazer a prévia. */
  #homes = new Map<number, Vec2>();

  #bannerActive = false;

  /**
   * Pausa a animação dos passos e mantém os personagens em pose neutra
   * enquanto o aviso de turno está visível na tela.
   */
  setBannerActive(active: boolean): void {
    if (this.#bannerActive === active) return;
    this.#bannerActive = active;
    if (active) {
      for (const sprite of this.#sprites.values()) {
        sprite.step = 1;
        this.#applyFrame(sprite);
      }
    }
  }

  /**
   * Desenha os baús do mapa, fechados e abertos.
   *
   * Redesenhado inteiro a cada mudança de estado, como o traçado do caminho:
   * são poucos por mapa, e a alternativa — casar sprite com identificador e
   * remendar as diferenças — custaria mais código do que economiza quadro.
   */
  async syncChests(state: MatchState): Promise<void> {
    const manifest = this.#manifest;
    if (!manifest || !state.chests?.length) {
      this.#chestLayer.removeChildren().forEach((child) => child.destroy());
      return;
    }

    // A folha do baú não é folha de evento do mapa, então ninguém a carregou
    // por nós. Carregada uma vez; as chamadas seguintes caem no cache do Pixi.
    await loadChestSheet(manifest);
    if (this.#destroyed) return;

    this.#chestLayer.removeChildren().forEach((child) => child.destroy());
    for (const chest of state.chests) {
      const sprite = chestSprite(manifest, chest.at, Boolean(chest.opened));
      if (sprite) this.#chestLayer.addChild(sprite);
    }
  }

  /** Limpa completamente todos os sprites de unidades do tabuleiro. */
  clearUnits(): void {
    // Primeiro desiste do que está sendo desenhado: uma sincronia em voo
    // acordaria da espera dela depois desta limpeza e repovoaria o tabuleiro
    // com as unidades que acabaram de sair de cena.
    this.#unitSync.cancel();
    for (const [, sprite] of this.#sprites) {
      sprite.root.destroy({ children: true });
    }
    this.#sprites.clear();
    this.#homes.clear();
    if (this.#previewUnitId !== null) this.#previewUnitId = null;
    if (this.#movingUnitId !== null) this.#movingUnitId = null;
  }

  syncUnits(
    state: MatchState,
    presentation: ReadonlyMap<number, UnitPresentation>,
  ): Promise<void> {
    return this.#unitSync.run((isCurrent) => this.#drawUnits(state, presentation, isCurrent));
  }

  /**
   * O desenho propriamente dito. Só a fila chama — ver `LatestOnly`.
   *
   * `isCurrent` é consultado depois de cada espera: a partir do instante em
   * que outra sincronia é pedida, esta para onde está. Terminar seria pintar o
   * tabuleiro com um estado que o jogo já deixou para trás.
   */
  async #drawUnits(
    state: MatchState,
    presentation: ReadonlyMap<number, UnitPresentation>,
    isCurrent: () => boolean,
  ): Promise<void> {
    const manifest = this.#manifest;
    if (!manifest) return;

    // Poda ativamente qualquer sprite que não seja mais uma unidade viva do estado atual.
    // Sem isto, reiniciar a partida ou mudar de semente deixava os monstros anteriores
    // como "fantasmas" visíveis no tabuleiro.
    const activeLivingIds = new Set(state.units.filter((u) => u.alive).map((u) => u.id));
    for (const [id, sprite] of this.#sprites) {
      if (!activeLivingIds.has(id)) {
        sprite.root.destroy({ children: true });
        this.#sprites.delete(id);
        this.#homes.delete(id);
        if (this.#previewUnitId === id) this.#previewUnitId = null;
      }
    }

    for (const unit of state.units) {
      if (!unit.alive) {
        this.#sprites.get(unit.id)?.root.destroy({ children: true });
        this.#sprites.delete(unit.id);
        this.#homes.delete(unit.id);
        if (this.#previewUnitId === unit.id) this.#previewUnitId = null;
        continue;
      }

      let sprite = this.#sprites.get(unit.id);
      if (!sprite) {
        const created = await this.#createUnitSprite(unit, presentation.get(unit.id));
        // A espera acima é a fresta por onde o boneco em dobro entrava. Se
        // outra sincronia foi pedida enquanto a textura carregava, este
        // desenho é de um estado que não existe mais: largar o que acabou de
        // nascer é mais barato do que pendurá-lo no tabuleiro para sempre.
        if (this.#destroyed || !isCurrent()) {
          created?.root.destroy({ children: true });
          return;
        }
        if (!created) continue;
        sprite = created;
        this.#sprites.set(unit.id, sprite);
        this.#unitLayer.addChild(sprite.root);
      }

      this.#homes.set(unit.id, unit.position);

      // Nem quem está andando nem quem já foi adiantado pela prévia podem ser
      // puxados de volta para a casa do estado: os dois estão, de propósito,
      // à frente do que a partida registrou.
      if (this.#movingUnitId !== unit.id && this.#previewUnitId !== unit.id) {
        sprite.root.position.set(unit.position.x * TILE_SIZE, unit.position.y * TILE_SIZE);
      }
      sprite.direction = unit.team === 'jogador' || unit.team === 'aliado' ? RIGHT : LEFT;
      this.#drawHpBar(sprite, unit);
      await this.#drawMarks(sprite, unit);
      if (this.#destroyed || !isCurrent()) return;

      // Unidade que já agiu sai de cor — a leitura de relance de quem ainda
      // pode jogar é metade do ritmo de um turno. Tingir não bastava: multiplicar
      // por um cinza escurece sem tirar a cor, e de longe a unidade gasta
      // continuava parecendo disponível. Dessaturar de fato é o que o GBA fazia.
      const spent = unit.hasActed && unit.team === state.phase;
      this.#setSpent(sprite, spent);

      if (this.#bannerActive) {
        sprite.step = 1;
        this.#applyFrame(sprite);
      }
    }

    // Quem está mais abaixo no mapa desenha por cima, para a sobreposição
    // parecer profundidade.
    this.#unitLayer.children.sort((a, b) => a.y - b.y);
  }

  #homeOf(unitId: number): Vec2 | undefined {
    return this.#homes.get(unitId);
  }

  /**
   * Tira ou devolve a cor da unidade conforme ela já tenha agido.
   *
   * Só o personagem muda: havia também um quadrado escuro cobrindo a casa
   * inteira, e ele marcava o chão em vez de quem está lá — dois avisos para o
   * mesmo fato, com o de baixo escondendo o terreno.
   */
  #setSpent(sprite: UnitSprite, spent: boolean): void {
    if (sprite.spent === spent) return;
    sprite.spent = spent;

    if (spent) {
      // O mesmo filtro serve a todas: o cinza é igual para qualquer unidade, e
      // um filtro novo por unidade por turno só acumulava objetos.
      if (!this.#spentFilter) {
        const grey = new ColorMatrixFilter();
        grey.desaturate();
        // `desaturate` só tira a cor; escurecer um pouco separa a unidade gasta
        // do terreno claro, onde cinza-claro sobre areia some.
        grey.brightness(0.72, true);
        this.#spentFilter = grey;
      }
      sprite.body.filters = [this.#spentFilter];
    } else {
      sprite.body.filters = [];
    }
  }

  /**
   * Caminha até o destino já na escolha, antes do menu de ação.
   *
   * É a ordem do Fire Emblem de GBA: você marca a casa, a unidade anda até lá e
   * só então o menu abre — no lugar onde ela está de fato. Andar depois de
   * escolher a ação, como antes, invertia a leitura: o menu falava de uma
   * posição que ainda não existia na tela.
   *
   * O estado da partida não muda aqui: isto é só o desenho adiantando-se à
   * jogada, que ainda pode ser desfeita com `cancelPreview`.
   */
  async previewMove(unitId: number, path: readonly Vec2[]): Promise<void> {
    if (path.length <= 1) {
      this.#previewUnitId = unitId;
      return;
    }
    await this.animateMove(unitId, path);
    this.#previewUnitId = unitId;
  }

  /** Desfaz a prévia: a unidade volta para onde o estado diz que ela está. */
  cancelPreview(): void {
    const unitId = this.#previewUnitId;
    this.#previewUnitId = null;
    if (unitId === null) return;

    const sprite = this.#sprites.get(unitId);
    const home = this.#homeOf(unitId);
    if (sprite && home) {
      sprite.root.position.set(home.x * TILE_SIZE, home.y * TILE_SIZE);
      this.#unitLayer.children.sort((a, b) => a.y - b.y);
    }
  }

  /** Confirma a prévia: o sprite já está no lugar, nada a reanimar. */
  commitPreview(): void {
    this.#previewUnitId = null;
  }

  /** Existe uma prévia em curso para esta unidade? */
  hasPreview(unitId: number): boolean {
    return this.#previewUnitId === unitId;
  }

  /**
   * Anima o deslocamento da unidade tile por tile com passos animados.
   *
   * A casa a cada 150 ms, e não 130: com o passo mais curto o efeito sonoro do
   * acervo — que dura 800 ms — disparava cinco vezes antes de a primeira cópia
   * terminar, e a caminhada virava um chiado só.
   */
  async animateMove(unitId: number, path: readonly Vec2[], speedMs = 150): Promise<void> {
    const sprite = this.#sprites.get(unitId);
    if (!sprite || path.length <= 1) return;

    while (this.#bannerActive) {
      await new Promise((r) => setTimeout(r, 60));
    }

    this.#movingUnitId = unitId;

    try {
      if (this.#reducedMotion) {
        const end = path[path.length - 1]!;
        sprite.root.position.set(end.x * TILE_SIZE, end.y * TILE_SIZE);
        return;
      }

      for (let i = 0; i < path.length - 1; i++) {
        const from = path[i]!;
        const to = path[i + 1]!;

        // Passo de teleporte: some de um lado e aparece do outro. Deslizar
        // entre as duas bocas atravessaria as paredes que o portal existe para
        // contornar.
        if (Math.abs(to.x - from.x) + Math.abs(to.y - from.y) !== 1) {
          this.onTeleport?.();
          await this.#warp(sprite, to);
          if (this.#gone(sprite)) return;
          this.#follow(to);
          continue;
        }

        this.onStep?.();
        const dx = to.x - from.x;
        const dy = to.y - from.y;
        if (dx > 0) sprite.direction = RIGHT;
        else if (dx < 0) sprite.direction = LEFT;
        else if (dy > 0) sprite.direction = DOWN;
        else if (dy < 0) sprite.direction = UP;

        const startX = from.x * TILE_SIZE;
        const startY = from.y * TILE_SIZE;
        const targetX = to.x * TILE_SIZE;
        const targetY = to.y * TILE_SIZE;

        const duration = speedMs;
        const startTime = performance.now();

        await new Promise<void>((resolve) => {
          const stepAnim = () => {
            if (this.#gone(sprite)) {
              resolve();
              return;
            }
            const now = performance.now();
            const elapsed = now - startTime;
            const progress = Math.min(1, elapsed / duration);

            sprite.root.position.set(
              startX + (targetX - startX) * progress,
              startY + (targetY - startY) * progress,
            );

            sprite.step = Math.floor(elapsed / (duration / 4)) % 4;
            this.#applyFrame(sprite);

            if (progress < 1) {
              requestAnimationFrame(stepAnim);
            } else {
              sprite.root.position.set(targetX, targetY);
              resolve();
            }
          };
          requestAnimationFrame(stepAnim);
        });

        if (this.#gone(sprite)) return;
        this.#follow(to);
      }

      sprite.step = 1;
      this.#applyFrame(sprite);
      this.#unitLayer.children.sort((a, b) => a.y - b.y);
    } finally {
      this.#movingUnitId = null;
    }
  }

  /**
   * O tabuleiro (ou a unidade) foi desmontado no meio de uma animação.
   *
   * Troca de mapa, contexto WebGL refeito, unidade morta: o sprite já não tem
   * posição. Mexer nele estouraria dentro do `requestAnimationFrame`, a promessa
   * nunca se cumpriria, e a jogada que espera por ela ficaria parada para
   * sempre.
   */
  #gone(sprite: UnitSprite): boolean {
    return this.#destroyed || sprite.root.destroyed;
  }

  /** A travessia do portal: apaga, muda de casa, reacende. */
  async #warp(sprite: UnitSprite, to: Vec2): Promise<void> {
    const place = (): void => {
      sprite.root.position.set(to.x * TILE_SIZE, to.y * TILE_SIZE);
      sprite.root.alpha = 1;
    };

    if (this.#reducedMotion) {
      place();
      return;
    }

    const fade = (from: number, target: number, duration: number): Promise<void> =>
      new Promise<void>((resolve) => {
        const started = performance.now();
        const frame = (): void => {
          if (this.#gone(sprite)) {
            resolve();
            return;
          }
          const progress = Math.min(1, (performance.now() - started) / duration);
          sprite.root.alpha = from + (target - from) * progress;
          if (progress < 1) requestAnimationFrame(frame);
          else resolve();
        };
        requestAnimationFrame(frame);
      });

    await fade(1, 0, 140);
    if (this.#gone(sprite)) return;
    place();
    sprite.root.alpha = 0;
    await fade(0, 1, 160);
    if (this.#gone(sprite)) return;
    sprite.root.alpha = 1;
  }

  async #createUnitSprite(
    unit: Unit,
    presentation: UnitPresentation | undefined,
  ): Promise<UnitSprite | null> {
    const manifest = this.#manifest;
    if (!manifest || !presentation) return null;

    const size = normalizeSize(unit.size);

    const root = new Container();
    const body = new Sprite();
    // A folha tem 48 de largura por quadro, mas o desenho ocupa mais altura que
    // a casa; ancorar embaixo faz o pé cair no centro do tile. Num corpo de
    // mais de uma casa o mesmo vale para a área inteira: o centro da base.
    body.anchor.set(0.5, 1);
    body.position.set((size.w * TILE_SIZE) / 2, size.h * TILE_SIZE + 6);

    const hpBar = new Graphics();
    const marks = new Container();
    root.addChild(body, hpBar, marks);

    const facing = unit.team === 'jogador' || unit.team === 'aliado' ? RIGHT : LEFT;

    // Monstro sem folha de caminhada entra com o desenho de combate. Tentado
    // primeiro para não cair na folha de reserva quando o battler existe.
    const art = presentation.battlerOnMap ? this.#battlerArt(presentation.battler) : null;
    if (art) {
      const texture = await Assets.load<Texture>(art.url);
      body.texture = texture;
      // A área já foi escolhida pelo tamanho do desenho (ver `mapArtFootprint`);
      // aqui o desenho só é encaixado nela.
      const scale = mapArtBodyScale(art.width, art.height, mapArtFootprint(art.width, art.height));
      body.scale.set(facing === RIGHT ? -scale : scale, scale);
      const sprite: UnitSprite = {
        root, body, hpBar, marks, markKey: '', size, spent: false, walk: null, direction: facing, step: 0,
      };
      return sprite;
    }

    const entry = manifest.images['characters']?.[presentation.sprite.sheet];
    if (!entry?.grid) return null;

    const texture = await Assets.load<Texture>(
      `${ASSETS_BASE}/img/characters/${entry.file}`,
    );

    const sprite: UnitSprite = {
      root, body, hpBar, marks, markKey: '', size, spent: false,
      walk: { sheet: texture, grid: entry.grid, character: presentation.sprite.index },
      direction: facing,
      step: 0,
    };
    this.#applyFrame(sprite);
    return sprite;
  }

  /**
   * Onde mora o desenho de combate de um monstro, e de que tamanho ele é.
   *
   * Duas galerias, nesta ordem: a de combate lateral primeiro, porque a figura
   * é menor e está desenhada de perfil — que é como a unidade se apresenta num
   * tabuleiro visto de cima. A de frente é a reserva, para o monstro que só
   * existe lá.
   */
  #battlerArt(battler: string): { url: string; width: number; height: number } | null {
    const manifest = this.#manifest;
    if (!manifest) return null;

    const art = battlerArt(manifest, battler);
    return art ? { url: art.url, width: art.entry.width, height: art.entry.height } : null;
  }

  #applyFrame(sprite: UnitSprite): void {
    const walk = sprite.walk;
    // Figura parada: não há quadro seguinte para trocar. O que muda com a
    // direção é o lado para onde ela olha, e isso é espelho, não animação.
    if (!walk) {
      const scale = Math.abs(sprite.body.scale.y);
      sprite.body.scale.x = sprite.direction === RIGHT ? -scale : scale;
      return;
    }

    const frames = walkFrames(walk.grid, walk.character, sprite.direction);
    const frame = frames[sprite.step % frames.length] ?? frames[1]!;
    const rect = frameRect(walk.grid, frame);
    // Do cache, e não novo: isto roda a cada 260ms por unidade, a partida toda.
    sprite.body.texture = frameTexture(walk.sheet.source, rect.x, rect.y, rect.width, rect.height);
  }

  /**
   * As condições da unidade, em ícones no canto de cima.
   *
   * A leitura de relance que a ficha não dá: quem está dormindo, quem está
   * envenenado, sem precisar parar o cursor em cada peça. Os ícones são os da
   * folha do acervo — os mesmos da ficha e do diário —, para que o jogador
   * aprenda um desenho só por condição.
   */
  async #drawMarks(sprite: UnitSprite, unit: Unit): Promise<void> {
    const key = unit.states.map((s) => s.condition).join(',');
    if (key === sprite.markKey) return;
    sprite.markKey = key;
    sprite.marks.removeChildren().forEach((child) => child.destroy());
    if (unit.states.length === 0) return;

    const sheet = await this.#loadIconSheet();
    if (!sheet || this.#destroyed || sprite.markKey !== key) return;

    unit.states.slice(0, MAX_MARKS).forEach((state, i) => {
      const icon = CONDITIONS[state.condition]?.icon;
      if (icon === undefined) return;
      const mark = new Sprite(
        frameTexture(
          sheet.source,
          (icon % ICON_COLUMNS) * ICON_SIDE,
          Math.floor(icon / ICON_COLUMNS) * ICON_SIDE,
          ICON_SIDE,
          ICON_SIDE,
        ),
      );
      mark.width = MARK_SIDE;
      mark.height = MARK_SIDE;
      mark.position.set(2 + i * (MARK_SIDE - 2), 2);
      sprite.marks.addChild(mark);
    });
  }

  #loadIconSheet(): Promise<Texture | null> {
    this.#iconSheet ??= Assets.load<Texture>(`${ASSETS_BASE}/img/system/IconSet.webp`)
      .then((texture) => {
        texture.source.scaleMode = 'nearest';
        return texture;
      })
      .catch(() => null);
    return this.#iconSheet;
  }

  #drawHpBar(sprite: UnitSprite, unit: Unit): void {
    // Um corpo de duas casas leva uma barra de duas casas: uma tarja de meia
    // casa embaixo de um dragão não se lê como sendo dele.
    const width = sprite.size.w * TILE_SIZE - 10;
    const ratio = Math.max(0, Math.min(1, unit.currentHp / unit.stats.hp));
    const color =
      unit.team === 'jogador' ? 0x5fd08a : unit.team === 'aliado' ? 0x5fa8d8 : 0xd8564f;

    const baseline = sprite.size.h * TILE_SIZE - 6;

    sprite.hpBar.clear();
    sprite.hpBar.rect(5, baseline, width, 4).fill({ color: 0x0b0d14, alpha: 0.85 });
    if (ratio > 0) {
      sprite.hpBar.rect(5, baseline, width * ratio, 4).fill({ color });
    }
  }

  // --- câmera e laço ------------------------------------------------------

  get zoom(): number {
    return this.#zoom;
  }

  /**
   * Troca o afastamento da câmera, centrando no cursor.
   *
   * Centrar, e não apenas manter em vista: mudar de escala mexe em cada pixel
   * da tela ao mesmo tempo, e se o ponto de referência escorregar junto o
   * jogador perde de vista onde estava. Com o cursor no meio, o mapa cresce e
   * encolhe em volta dele.
   */
  setZoom(zoom: number): void {
    if (zoom === this.#zoom) return;
    this.#zoom = zoom;
    this.#world.scale.set(zoom);
    this.#center(this.#cursorAt);
  }

  /** Põe a casa no meio da tela, sem deixar a borda do mapa entrar na tela. */
  #center(at: Vec2): void {
    const view = this.app.screen;
    const scale = this.#zoom;
    const half = TILE_SIZE / 2;

    let x = view.width / 2 - (at.x * TILE_SIZE + half) * scale;
    let y = view.height / 2 - (at.y * TILE_SIZE + half) * scale;

    const worldWidth = this.#tilemap.pixelWidth * scale;
    const worldHeight = this.#tilemap.pixelHeight * scale;
    x = worldWidth <= view.width ? (view.width - worldWidth) / 2 : Math.min(0, Math.max(view.width - worldWidth, x));
    y = worldHeight <= view.height ? (view.height - worldHeight) / 2 : Math.min(0, Math.max(view.height - worldHeight, y));

    this.#camX = x;
    this.#camY = y;

    if (!this.#camReady) {
      this.#camReady = true;
      this.#world.position.set(Math.round(x), Math.round(y));
    }
  }

  /** Mantém o cursor em vista, sem centralizar à força a cada passo. */
  #follow(at: Vec2): void {
    const view = this.app.screen;
    const scale = this.#zoom;
    const margin = TILE_SIZE * 2 * scale;

    const px = at.x * TILE_SIZE * scale;
    const py = at.y * TILE_SIZE * scale;
    let { x, y } = this.#world.position;

    if (px + x < margin) x = margin - px;
    if (px + x > view.width - margin - TILE_SIZE * scale) x = view.width - margin - TILE_SIZE * scale - px;
    if (py + y < margin) y = margin - py;
    if (py + y > view.height - margin - TILE_SIZE * scale) y = view.height - margin - TILE_SIZE * scale - py;

    const worldWidth = this.#tilemap.pixelWidth * scale;
    const worldHeight = this.#tilemap.pixelHeight * scale;
    x = worldWidth <= view.width ? (view.width - worldWidth) / 2 : Math.min(0, Math.max(view.width - worldWidth, x));
    y = worldHeight <= view.height ? (view.height - worldHeight) / 2 : Math.min(0, Math.max(view.height - worldHeight, y));

    this.#camX = x;
    this.#camY = y;

    // A primeira mira acontece antes de existir quadro: ali a câmera assume o
    // destino de uma vez, senão o mapa abriria deslizando do canto.
    if (!this.#camReady) {
      this.#camReady = true;
      this.#world.position.set(Math.round(x), Math.round(y));
    }
  }

  /**
   * Aproxima a câmera do destino.
   *
   * A interpolação usa o tempo decorrido, e não uma fração fixa por quadro:
   * com fração fixa a câmera anda mais depressa em tela de 144 Hz do que em
   * uma de 60 Hz, e a suavidade some justamente onde deveria ser melhor.
   */
  #stepCamera(deltaMs: number): void {
    if (this.#reducedMotion) {
      this.#world.position.set(Math.round(this.#camX), Math.round(this.#camY));
      return;
    }

    const current = this.#world.position;
    const dx = this.#camX - current.x;
    const dy = this.#camY - current.y;

    // Perto o bastante: encaixa e para, para não ficar tremendo em subpixel.
    if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) {
      this.#world.position.set(Math.round(this.#camX), Math.round(this.#camY));
      return;
    }

    const factor = 1 - Math.exp(-deltaMs / CAMERA_TAU_MS);
    this.#world.position.set(
      Math.round(current.x + dx * factor),
      Math.round(current.y + dy * factor),
    );
  }

  #tick(deltaMs: number): void {
    this.#tilemap.update(deltaMs);
    this.#stepCamera(deltaMs);

    if (this.#reducedMotion) {
      this.#cursorMark.alpha = 1;
    } else {
      this.#blink += deltaMs;
      // Pisca em torno de 2 Hz, como o cursor do GBA.
      this.#cursorMark.alpha = 0.45 + 0.55 * Math.abs(Math.sin(this.#blink / 260));
    }

    if (this.#gates.children.length > 0) {
      this.#gates.alpha = this.#reducedMotion
        ? 1
        : 0.78 + 0.22 * Math.abs(Math.sin(this.#blink / 520));
    }

    if (!this.#bannerActive) {
      this.#walk += deltaMs;
      if (this.#walk >= 260) {
        this.#walk -= 260;
        for (const sprite of this.#sprites.values()) {
          sprite.step = (sprite.step + 1) % 4;
          this.#applyFrame(sprite);
        }
      }
    }
  }

  // --- ponteiro -----------------------------------------------------------

  #bindPointer(): void {
    const canvas = this.app.canvas;
    const toTile = (event: PointerEvent): Vec2 | null => {
      const rect = canvas.getBoundingClientRect();
      const x = Math.floor(((event.clientX - rect.left) - this.#world.position.x) / (TILE_SIZE * this.#zoom));
      const y = Math.floor(((event.clientY - rect.top) - this.#world.position.y) / (TILE_SIZE * this.#zoom));
      const map = this.#map;
      if (!map || x < 0 || y < 0 || x >= map.width || y >= map.height) return null;
      return { x, y };
    };

    canvas.addEventListener('pointermove', (event) => {
      const tile = toTile(event);
      if (tile) this.onTileHover?.(tile);
    });
    canvas.addEventListener('pointerdown', (event) => {
      const tile = toTile(event);
      if (tile) this.onTileClick?.(tile);
    });
  }

  destroy(): void {
    if (this.#destroyed) return;
    this.#destroyed = true;
    this.onContextLost = null;
    // Se `init` ainda não voltou, ela mesma destrói ao terminar.
    if (this.#initialized) {
      this.app.destroy(true, { children: true });
    }
    this.#spentFilter?.destroy();
    this.#spentFilter = null;
  }
}
